import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
} from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { idpApi } from '../../../../services/api/idpApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { IDP_PRIORITIES } from '../../../../constants/idpPriorities';
import useIDPStoreRepository from '../../../../store/idpStoreRepository';
import { BackButton } from '../../../common/styles';
import LoadingSpinner from '../../../common/LoadingSpinner';
import SelectEmployee from './SelectEmployee';
import SelectTrackingMethod from './SelectTrackingMethod';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  MainContainer,
} from '../../styles';

const STEPS = ['Select Individual', 'Select Tracking Method', 'Review & Create'];

const IDPWizard = ({ onBack, onComplete }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const updateGoalInStore = useIDPStoreRepository((state) => state.updateGoal);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMeasurementType, setSelectedMeasurementType] = useState(null);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [goalStatuses, setGoalStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const [types, statuses] = await Promise.all([
        idpApi.getMeasurementTypes(),
        idpApi.getGoalStatuses(),
      ]);
      setMeasurementTypes(types);
      setGoalStatuses(statuses);
    } catch (err) {
      showAlert('Failed to load initial data', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === STEPS.length - 1) {
      handleCreate();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onBack();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleCreate = async () => {
    if (!selectedEmployee || !selectedMeasurementType) {
      showAlert('Please select both an employee and a tracking method', 'warning');
      return;
    }

    try {
      setIsCreating(true);

      // Find the "Not Started" status as the default
      const notStartedStatus = goalStatuses.find(s => s.name === 'Not Started');

      // Create a minimal goal with the selected employee and measurement type
      // The user will complete the rest in the form
      const goalData = {
        employeeId: selectedEmployee.id,
        measurementTypeId: selectedMeasurementType.id,
        title: 'New Development Goal',
        description: '',
        priority: IDP_PRIORITIES.LOW,
        currentStatusId: notStartedStatus?.id,
        deadlineAt: null,
        assignedAt: new Date().toISOString(),
        actions: [],
        resources: [],
      };

      const result = await idpApi.saveDevelopmentGoal(goalData);

      if (result && result.id) {
        updateGoalInStore(result);
        showAlert('Development plan created! Now add the goal details.', 'success');
        onComplete(result.id);
      } else {
        showAlert('Failed to create development plan', 'error');
      }
    } catch (err) {
      showAlert(err.message || 'Failed to create development plan', 'error', err);
    } finally {
      setIsCreating(false);
    }
  };

  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return !selectedEmployee;
      case 1:
        return !selectedMeasurementType;
      case 2:
        return isCreating;
      default:
        return false;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <SelectEmployee
            selectedEmployee={selectedEmployee}
            onSelect={setSelectedEmployee}
          />
        );
      case 1:
        return (
          <SelectTrackingMethod
            measurementTypes={measurementTypes}
            selectedType={selectedMeasurementType}
            onSelect={setSelectedMeasurementType}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Review Your Selection
            </Typography>

            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Individual
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedEmployee?.fullName || 'Not selected'}
                </Typography>
                {selectedEmployee?.email && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedEmployee.email}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Tracking Method
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedMeasurementType?.name || 'Not selected'}
                </Typography>
                {selectedMeasurementType?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedMeasurementType.description}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Typography variant="body2" color="text.secondary">
              Click "Create Development Plan" to create the goal. You'll be able to add
              more details like title, description, actions, and resources in the next step.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  if (isLoading && measurementTypes.length === 0) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            <FaArrowLeft /> Back to Development Plans
          </BackButton>
          <Title>Create Development Plan</Title>
        </HeaderLeft>
      </Header>

      <MainContainer>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 900,
            mx: 'auto',
            p: 4,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            backgroundColor: 'white',
          }}
        >
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ minHeight: 300 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, borderTop: '1px solid #e2e8f0', mt: 3 }}>
            <Button
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled()}
            >
              {activeStep === STEPS.length - 1 ? (
                isCreating ? 'Creating...' : 'Create Development Plan'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Paper>
      </MainContainer>
    </Container>
  );
};

export default IDPWizard;
